import {
  AimOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  DollarOutlined,
  LineChartOutlined,
  PhoneOutlined,
  PieChartOutlined,
  SearchOutlined,
  TableOutlined,
  TeamOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { Button, DatePicker, Empty, Input, Segmented, Select, Skeleton, Table } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  formatCurrency,
  formatWithCommas,
  getUserType,
} from "../../assets/helpers";
import ActionCodesModal from "../../components/ActionCodesModal/ActionCodesModal";
import { TelecallerPerformanceModal } from "../../components/TelecallerPerformanceModal/TelecallerPerformanceModal";
import ActionCodeTable from "../../components/TelecallersTable/ActionCodeCoverageTable";
import AppliedFilterChips from "../../Reuseable/actionHeader/AppliedFilterChips";
import FilterDropdown from "../../Reuseable/actionHeader/Filter";
import {
  fetchDashboard,
  fetchDashboardOverview,
  fetchPerformanceReportTable,
  fetchSupervisorReportOverview,
  fetchTeamleadOverview,
} from "../../store/dashboardSlice";
import { clearQuery, fetchUserFilters, setQuery } from "../../store/filters";
import { fetchCaseIntencity } from "../../store/portfolioSlice";
import {
  fetchTelecallerActionCodes,
  fetchTelecallerDashboard,
  fetchTelecallerReport,
} from "../../store/telecaller/telecallerDashboard";
import ActionCodeInsight from "./components/ActionCodeInsight";
import IntensityStrip from "./components/IntensityStrip";
import KpiCard from "./components/KpiCard";
import PanelCard from "./components/PanelCard";
import RecoveryPanel from "./components/RecoveryPanel";
import StatusBreakdown from "./components/StatusBreakdown";
import TrendAreaChart from "./components/TrendAreaChart";
import "./DashboardPage.scss";

const ROLE_LABELS = {
  telecaller: "Telecaller",
  supervisor: "Supervisor",
  agency: "Agency",
  team_lead: "Team Lead",
  field_agent: "Field Agent",
  superuser: "Administrator",
};

/* A failed request and an empty result used to look identical: both drew a
   blank panel. This says which one happened, and offers a retry when it was
   the request rather than the data. */
const PanelState = ({ error, onRetry, description }) => (
  <div className="dsh-panel-state">
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        error ? (
          <span className="dsh-panel-state__error">
            {typeof error === "string" ? error : "Could not load this report"}
          </span>
        ) : (
          description
        )
      }
    />
    {error && onRetry && (
      <Button size="small" className="dsh-panel-state__retry" onClick={onRetry}>
        Try again
      </Button>
    )}
  </div>
);

const DashboardPage = React.memo(() => {
  const {
    data: dashboardData,
    loading,
    overview,
    overviewLoading,
    supervisorReportCoverageChart,
    teamLeadOverviewChart,
    team_lead_loading,
    team_lead_error,
    supervisorReportError,
    supervisorReportLoading,
    performance_report_table_loading,
    performance_report_table,
  } = useSelector((state) => state.dashboard);
  const { format } = useSelector((state) => state.priceFormat);
  const { query } = useSelector((state) => state.filters);
  const {
    tele_dashboard,
    tele_dash_loading,
    tele_report_overview,
    tele_report_overview_loading,
  } = useSelector((state) => state.telecallerDashboard);
  const { user } = useSelector((state) => state.auth);
  const { case_intensity_counts, case_intensity_counts_loading } = useSelector(
    (state) => state?.portfolio,
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [performancePagination, setPerformancePagination] = useState({
    page: 1,
    page_size: 5,
  });
  const [tableFilters, setTableFilters] = useState({ search: "", date: null });
  const [performanceView, setPerformanceView] = useState("activity");
  const [reportView, setReportView] = useState("telecaller");
  const [selectedFilter, setSelectedFilter] = useState("Performance");
  const [selectedStat, setSelectedStat] = useState("Cases");
  const [openActionCodes, setOpenActionCodes] = useState(false);
  const [performanceReportRowData, setPerformanceReportRowData] =
    useState(null);

  const userType = getUserType(user);
  const statsLoading = loading || tele_dash_loading;
  const reportLoading = overviewLoading || tele_report_overview_loading;


  const palette = useMemo(
    () => [
      "#3070F0",
      "#8FC4FF",
      "#9E80FF",
      "#FFCB72",
      "#7EE0E0",
      "#00C7C7",
      "#007272",
      "#F682C9",
      "#64C75A",
      "#C3AFFF",
      "#A060DC",
      "#F15A41",
      "#F8B7F8",
      "#3070F0",
      "#8FC4FF",
      "#9E80FF",
      "#FFCB72",
      "#7EE0E0",
      "#00C7C7",
      "#007272",
    ],
    [],
  );

  const getStats = useCallback(
    (type) => {
      switch (type) {
        case "telecaller":
          return tele_dashboard ?? null;
        case "supervisor":
        case "agency":
          return dashboardData ?? null;
        default:
          return null;
      }
    },
    [tele_dashboard, dashboardData],
  );

  const getReport = useCallback(
    (type) => {
      switch (type) {
        case "telecaller":
          return tele_report_overview ?? null;
        case "supervisor":
        case "agency":
          return overview ?? null;
        default:
          return null;
      }
    },
    [tele_report_overview, overview],
  );

  const stats = getStats(userType);
  const report = getReport(userType);

  /* ------------------------------ charts data ----------------------------- */

  /* The team lead endpoint answers with one of two payloads depending on the
     deployment: the newer `overview` + `allocation_breakdown` (one entry per
     agent type), or the older `Stats: { allocated, resolved, pending }`.

     Reading only the new one left the panel plotting an empty array against a
     perfectly good 200 response - the donut summed to zero and fell through to
     "No case data available" with nothing on screen saying why. Both shapes
     are read here, so the panel works before and after the backend switches. */
  const legacyStats = useMemo(() => {
    const stats = teamLeadOverviewChart?.Stats || teamLeadOverviewChart?.stats;
    return stats && typeof stats === "object" ? stats : null;
  }, [teamLeadOverviewChart]);

  const teamLeadOverview = useMemo(() => {
    const overview = teamLeadOverviewChart?.overview;
    if (overview && Object.keys(overview).length > 0) return overview;
    if (!legacyStats) return {};

    const total = Number(legacyStats.total_cases) || 0;
    const resolved = Number(legacyStats.resolved?.count) || 0;

    /* `allocated` was renamed to `unresolved` and `pending` dropped; the old
       key is still read so a deployment on the previous build keeps working */
    const unresolvedBucket = legacyStats.unresolved ?? legacyStats.allocated;
    const unresolved =
      unresolvedBucket?.count !== undefined
        ? Number(unresolvedBucket.count) || 0
        : Math.max(total - resolved, 0);

    return {
      total_cases: total,
      total_resolved_cases: resolved,
      total_unresolved_cases: unresolved,
      overall_resolved_percent:
        legacyStats.resolved?.percentage ??
        (total ? Number(((resolved / total) * 100).toFixed(1)) : 0),
    };
  }, [teamLeadOverviewChart, legacyStats]);

  const allocationBreakdown = useMemo(() => {
    if (Array.isArray(teamLeadOverviewChart?.allocation_breakdown)) {
      return teamLeadOverviewChart.allocation_breakdown;
    }
    if (!legacyStats) return [];

    /* the old payload has no agent-type split, so its own three counts are
       the cut the donut plots */
    /* The payload carries `resolved` and `unresolved` (previously
       `allocated`); `pending` was removed, so it is only plotted when a
       response actually still contains it. */
    const unresolvedBucket = legacyStats.unresolved ?? legacyStats.allocated;

    return [
      {
        label: "Resolved",
        cases: { count: Number(legacyStats.resolved?.count) || 0 },
        percentage: legacyStats.resolved?.percentage,
      },
      {
        label: "Unresolved",
        cases: { count: Number(unresolvedBucket?.count) || 0 },
        percentage: unresolvedBucket?.percentage,
      },
      ...(legacyStats.pending
        ? [
            {
              label: "Pending",
              cases: { count: Number(legacyStats.pending?.count) || 0 },
              percentage: legacyStats.pending?.percentage,
            },
          ]
        : []),
    ];
  }, [teamLeadOverviewChart, legacyStats]);

  /* the old payload carries no money at all, so the POS cut has nothing to
     plot and is dropped rather than offered as a tab that draws nothing */
  const hasAmounts = useMemo(
    () =>
      allocationBreakdown.some(
        (entry) => entry?.amount?.total_odpos !== undefined &&
          entry?.amount?.total_odpos !== null,
      ),
    [allocationBreakdown],
  );

  const teamLeadPortfolio = teamLeadOverviewChart?.portfolio?.name || "";

  /* Only the newer payload splits cases by agent type; the `Stats` shape is
     already a resolved/unresolved split, so there "Cases" and "Resolution"
     would draw the identical donut. Offer a measure only where it is a
     genuinely different cut of the data. */
  const hasAgentSplit = useMemo(
    () => Array.isArray(teamLeadOverviewChart?.allocation_breakdown),
    [teamLeadOverviewChart],
  );

  const statOptions = useMemo(
    () => [
      ...(hasAgentSplit ? [{ label: "Cases", value: "Cases" }] : []),
      ...(hasAmounts ? [{ label: "Amount (POS)", value: "POS" }] : []),
      { label: "Resolution", value: "Resolution" },
    ],
    [hasAgentSplit, hasAmounts],
  );

  /* the active measure can stop existing when a different payload arrives */
  useEffect(() => {
    if (!statOptions.some((option) => option.value === selectedStat)) {
      setSelectedStat(statOptions[0]?.value || "Resolution");
    }
  }, [statOptions, selectedStat]);

  /* one cut of the breakdown: what the donut plots, what its centre says and
     how its figures are written */
  const teamLeadCut = useMemo(() => {
    if (selectedStat === "Resolution") {
      /* the API reports the resolved share, so it is shown as given and the
         other slice is its remainder rather than a second rounding */
      const resolvedPercent = teamLeadOverview.overall_resolved_percent;
      const hasPercent =
        resolvedPercent !== undefined && resolvedPercent !== null;

      return {
        labels: ["Resolved", "Unresolved"],
        data: [
          teamLeadOverview.total_resolved_cases || 0,
          teamLeadOverview.total_unresolved_cases || 0,
        ],
        percentages: hasPercent
          ? [
              Number(resolvedPercent),
              Number((100 - Number(resolvedPercent)).toFixed(1)),
            ]
          : [],
        colors: ["#36A546", "#FFA52F"],
        centerLabel: "Total Cases",
        subtitle: `${
          Number(teamLeadOverview.overall_resolved_percent) || 0
        }% resolved of ${formatWithCommas(
          teamLeadOverview.total_cases || 0,
        )} cases`,
      };
    }

    const isAmount = selectedStat === "POS";
    return {
      labels: allocationBreakdown.map(
        (entry, index) => entry?.label || `Agent type ${index + 1}`,
      ),
      data: allocationBreakdown.map((entry) =>
        isAmount
          ? Number(entry?.amount?.total_odpos) || 0
          : Number(entry?.cases?.count) || 0,
      ),
      /* amounts have their own share when the payload carries one; otherwise
         the case share is used, and failing that the donut derives it */
      percentages: allocationBreakdown.map((entry) =>
        isAmount
          ? entry?.amount?.percentage
          : entry?.cases?.percentage ?? entry?.percentage,
      ),
      colors: legacyStats
        ? ["#36A546", "#FFA52F", "#1F77F5"]
        : ["#1F77F5", "#FF6843", "#9E80FF", "#00C7C7"],
      centerLabel: isAmount ? "Total POS" : "Total Cases",
      /* the agents behind the split, which the old payload never carried */
      subtitle: allocationBreakdown.length
        ? allocationBreakdown
            .map((entry) =>
              /* the old payload has no agents behind the split, so it reports
                 the counts themselves rather than "0 agents" three times */
              entry?.agents_involved !== undefined &&
              entry?.agents_involved !== null
                ? `${entry?.label}: ${entry.agents_involved} agent${
                    entry.agents_involved === 1 ? "" : "s"
                  }`
                : `${entry?.label}: ${formatWithCommas(
                    entry?.cases?.count || 0,
                  )}`,
            )
            .join(" · ")
        : "Allocation by agent type",
      valueFormatter: isAmount
        ? (value) => formatCurrency(value, format)
        : undefined,
    };
  }, [selectedStat, allocationBreakdown, teamLeadOverview, legacyStats, format]);

  const overviewColors = useMemo(() => ["#36A546", "#FFA52F", "#1F77F5"], []);

  const categories = useMemo(
    () =>
      supervisorReportCoverageChart?.monthly_data?.map((m) =>
        m.month?.toUpperCase().slice(0, 3),
      ) || [],
    [supervisorReportCoverageChart],
  );

  const seriesData = useMemo(
    () => [
      {
        name: "Resolved",
        data:
          supervisorReportCoverageChart?.monthly_data?.map((m) => m.resolved) ||
          [],
      },
      {
        name: "Pending",
        data:
          supervisorReportCoverageChart?.monthly_data?.map((m) => m.pending) ||
          [],
      },
      {
        name: "Allocated",
        data:
          supervisorReportCoverageChart?.monthly_data?.map(
            (m) => m.allocated,
          ) || [],
      },
    ],
    [supervisorReportCoverageChart],
  );

  const telecallerCodes = useMemo(
    () => report?.telecaller_report?.codes || [],
    [report],
  );
  const fieldAgentCodes = useMemo(
    () => report?.field_agent_report?.codes || [],
    [report],
  );

  const activeCodes =
    reportView === "telecaller" ? telecallerCodes : fieldAgentCodes;
  const activeTotal =
    reportView === "telecaller"
      ? report?.telecaller_report?.total || 0
      : report?.field_agent_report?.total || 0;

  const dropdownOptions = useMemo(
    () => [{ label: "Performance", value: "Performance" }],
    [],
  );

  const validPerformanceTable = useMemo(
    () => [
      ...(performance_report_table?.results ?? []),
      ...(performance_report_table?.total
        ? [performance_report_table.total]
        : []),
    ],
    [performance_report_table],
  );

  /* -------------------------------- effects ------------------------------- */

  useEffect(() => {
    dispatch(fetchDashboard(query));
    dispatch(fetchDashboardOverview(query));
    dispatch(fetchSupervisorReportOverview(query));
    dispatch(fetchTeamleadOverview(query));
    dispatch(
      fetchPerformanceReportTable({
        ...query,
        page: performancePagination.page,
        page_size: performancePagination.page_size,
      }),
    );
    dispatch(fetchTelecallerActionCodes());
    dispatch(fetchCaseIntencity(query));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, JSON.stringify(query), user?.selectedRole]);

  useEffect(() => {
    dispatch(clearQuery());
    dispatch(fetchUserFilters());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchTelecallerDashboard(query));
    dispatch(fetchTelecallerReport(query));
  }, [dispatch, query, user?.selectedRole]);

  /* ------------------------------- handlers ------------------------------- */

  const handleFilterChange = useCallback((value) => {
    setSelectedFilter(value);
  }, []);

  const handleStatChange = useCallback((value) => {
    setSelectedStat(value);
  }, []);

  const loadPerformanceTable = useCallback(
    (overrides = {}) => {
      const next = {
        page: performancePagination.page,
        page_size: performancePagination.page_size,
        ...query,
        ...tableFilters,
        ...overrides,
      };
      if (!next.search) delete next.search;
      if (!next.date) delete next.date;
      dispatch(fetchPerformanceReportTable(next));
    },
    [dispatch, query, tableFilters, performancePagination],
  );

  const handleNavigate = (item) => {
    dispatch(setQuery({ intensity: item?.intensity }));
    navigate(`/allocation/cases`, { state: { preserveFilters: true } });
  };

  const handleNotCalledNavigate = () => {
    dispatch(setQuery({ not_called: true }));
    navigate(`/allocation/cases`, { state: { preserveFilters: true } });
  };

  const handleRowClick = async (actionCode) => {
    setOpenActionCodes((prev) => ({ ...prev, open: true, data: actionCode }));
  };

  const handlePerformanceReportRowClick = (text, record, key) => {
    setPerformanceReportRowData((prev) => ({
      ...prev,
      open: true,
      params: { telecallers: [record?.id], code: key },
    }));
  };

  /* ----------------------------- table columns ---------------------------- */

  const renderWithSkeleton = (text, fallback) => {
    if (performance_report_table_loading) {
      return <Skeleton.Input size="small" active />;
    }
    if (text === "Total") {
      return <span className="dsh-table__total">{text}</span>;
    }
    if (text === undefined || text === null || text.toString().trim() === "") {
      return fallback || "NA";
    }
    return typeof text === "number" ? formatWithCommas(text) : text;
  };

  const renderAgent = (_, record) => {
    if (performance_report_table_loading) {
      return <Skeleton.Input size="small" active />;
    }
    const name = record?.agent_name || record?.username || "Total";
    const initials = String(name)
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase();

    return (
      <div className="dsh-table__agent">
        <span className="dsh-table__avatar">{initials || "NA"}</span>
        <span className="dsh-table__agent-name">{name}</span>
      </div>
    );
  };

  const drillCell = (key) => (text, record) => {
    if (performance_report_table_loading) {
      return <Skeleton.Input size="small" active />;
    }
    const isEmpty =
      text === undefined || text === null || text.toString().trim() === "";
    return (
      <button
        type="button"
        className="dsh-table__drill"
        onClick={() => handlePerformanceReportRowClick(text, record, key)}
      >
        {isEmpty ? "NA" : renderWithSkeleton(text)}
      </button>
    );
  };

  const activityColumns = [
    {
      title: "Agent",
      dataIndex: "agent_name",
      key: "agent_name",
      render: renderAgent,
    },
    {
      title: "Allocated Cases",
      dataIndex: "allocated_cases",
      key: "allocated_cases",
      render: (text) => renderWithSkeleton(text),
    },
    {
      title: `Today Calls (${
        performance_report_table?.results?.[0]?.today_date || "NA"
      })`,
      dataIndex: "today_calls",
      key: "today_calls",
      render: (text) => renderWithSkeleton(text),
    },
    {
      title: `Yesterday Calls (${
        performance_report_table?.results?.[0]?.yesterday_date || "NA"
      })`,
      dataIndex: "yesterday_calls",
      key: "yesterday_calls",
      render: (text) => renderWithSkeleton(text),
    },
    {
      title: "Average Calls (Till Date)",
      dataIndex: "average_calls_till_date",
      key: "average_calls_till_date",
      render: (text) => renderWithSkeleton(text),
    },
    {
      title: "This Month Calls",
      dataIndex: "this_month_calls",
      key: "this_month_calls",
      render: (text) => renderWithSkeleton(text),
    },
    {
      title: "Total Calls",
      dataIndex: "total_calls",
      key: "total_calls",
      render: (text) => renderWithSkeleton(text),
    },
  ];

  const outcomeColumns = [
    {
      title: "Agent",
      dataIndex: "agent_name",
      key: "agent_name",
      render: renderAgent,
    },
    {
      title: "Paid Amount",
      dataIndex: "paid_amount",
      key: "paid_amount",
      render: drillCell("paid_amount "),
    },
    {
      title: "Paid",
      dataIndex: "PAID",
      key: "PAID",
      render: drillCell("PAID"),
    },
    {
      title: "PTP Amount",
      dataIndex: "ptp_amount",
      key: "ptp_amount",
      render: drillCell("PTP"),
    },
    {
      title: "PTP",
      dataIndex: "PTP",
      key: "PTP",
      render: drillCell("PTP"),
    },
    {
      title: "Call Back",
      dataIndex: "CALLBACK",
      key: "CALLBACK",
      render: drillCell("CALLBACK"),
    },
    {
      title: "Non Contact",
      dataIndex: "NON CONTACT",
      key: "NON CONTACT",
      render: drillCell("NON CONTACT"),
    },
    {
      title: "Ringing",
      dataIndex: "RINGING",
      key: "RINGING",
      render: drillCell("RINGING"),
    },
  ];

  const isActivityView = performanceView === "activity";

  /* --------------------------------- render -------------------------------- */

  return (
    <div className="dashboardPageMain">
      {/* ------------------------------- hero ------------------------------- */}
      <header className="dsh-hero">
        <div className="dsh-hero__bg" aria-hidden="true" />
        <div className="dsh-hero__content">
          <div className="dsh-hero__intro">
            <h1>Dashboard</h1>
            <span className="dsh-hero__eyebrow">
              <i />
              {ROLE_LABELS[userType] || "Collections"}
            </span>
          </div>

          <div className="dsh-hero__tools">
            <FilterDropdown variant="2" />
          </div>
        </div>

        {/* what is filtering the page, so the dropdown is not the only way
            to know - removable, and the same row the analysis header uses */}
        <AppliedFilterChips />
      </header>

      {/* ------------------------------ KPI grid ---------------------------- */}
      <div className="dsh-kpi-grid">
        <KpiCard
          title="Total Cases"
          icon={<DatabaseOutlined />}
          accent="brand"
          loading={statsLoading}
          value={formatWithCommas(stats?.total_cases?.count)}
          trend={dashboardData?.total_cases?.case_percent}
          footerLabel="Amount Overdue"
          footerValue={formatCurrency(
            stats?.total_cases?.amount_overdue,
            format,
          )}
        />

        <KpiCard
          title="Allocated Stats"
          icon={<UserSwitchOutlined />}
          accent="blue"
          variant="split"
          loading={statsLoading}
          value={formatWithCommas(stats?.allocated_stats?.total)}
          segments={[
            {
              name: "Telecaller",
              value: stats?.allocated_stats?.telecaller_percentage,
            },
            {
              name: "Field Agent",
              value: stats?.allocated_stats?.field_agent_percentage,
            },
          ]}
        />

        <KpiCard
          title="Unallocated Stats"
          icon={<TeamOutlined />}
          accent="amber"
          variant="split"
          loading={statsLoading}
          value={formatWithCommas(stats?.unallocated_stats?.total)}
          segments={[
            {
              name: "Telecaller",
              value: stats?.unallocated_stats?.telecaller_percentage,
            },
            {
              name: "Field Agent",
              value: stats?.unallocated_stats?.field_agent_percentage,
            },
          ]}
        />

        <KpiCard
          title="Coverage Stats"
          icon={<AimOutlined />}
          accent="green"
          variant="split"
          loading={statsLoading}
          value={formatWithCommas(stats?.coverage_stats?.total)}
          segments={[
            {
              name: "Telecaller",
              value: stats?.coverage_stats?.telecaller_percentage,
            },
            {
              name: "Field Agent",
              value: stats?.coverage_stats?.field_agent_percentage,
            },
          ]}
        />

        {(user?.is_tc || user?.is_agency || user?.is_supervisor) && (
          <KpiCard
            title="Not Called"
            icon={<PhoneOutlined />}
            accent="violet"
            loading={statsLoading}
            value={formatWithCommas(stats?.not_called)}
            footerLabel="Open in allocation"
            footerValue="View cases →"
            onClick={handleNotCalledNavigate}
            hint="View all cases that have not been contacted yet"
          />
        )}

        {user?.is_tc && (
          <>
            <KpiCard
              title="PTP Momentum"
              icon={<LineChartOutlined />}
              accent="teal"
              variant="split"
              loading={statsLoading}
              value={formatWithCommas(
                stats?.telecaller_activity?.this_month_ptp,
              )}
              segments={[
                {
                  name: "This Month",
                  value: stats?.telecaller_activity?.this_month_ptp,
                },
                {
                  name: "Last Month",
                  value: stats?.telecaller_activity?.last_month_ptp,
                },
              ]}
            />

            <KpiCard
              title="Call Activity"
              icon={<PhoneOutlined />}
              accent="blue"
              variant="split"
              loading={statsLoading}
              value={formatWithCommas(stats?.telecaller_activity?.todays_call)}
              segments={[
                {
                  name: "Today",
                  value: stats?.telecaller_activity?.todays_call,
                },
                {
                  name: "Yesterday",
                  value: stats?.telecaller_activity?.yesterday_call,
                },
              ]}
            />

            <KpiCard
              title="Collected Amount"
              icon={<DollarOutlined />}
              accent="green"
              loading={statsLoading}
              value={formatCurrency(
                stats?.telecaller_activity?.collected_amount,
                format,
              )}
              footerLabel="Total PTP"
              footerValue={formatWithCommas(
                stats?.telecaller_activity?.total_ptp,
              )}
            />
          </>
        )}
      </div>

      {/* ----------------------------- recovery ----------------------------- */}
      <RecoveryPanel
        data={stats?.recovery_stats || {}}
        loading={statsLoading}
      />

      {/* ---------------------------- intensity ----------------------------- */}
      {(user?.is_tc || user?.is_agency || user?.is_supervisor) && (
        <IntensityStrip
          items={case_intensity_counts?.intensity || []}
          loading={case_intensity_counts_loading || loading}
          onSelect={handleNavigate}
        />
      )}

      {/* ------------------------ performance report ------------------------ */}
      {(user?.is_agency || user?.is_supervisor) && (
        <PanelCard
          title="Telecaller Performance Report"
          subtitle={
            isActivityView
              ? "Call throughput per agent"
              : "Payment and promise outcomes per agent"
          }
          icon={<TableOutlined />}
          accent="violet"
          padded={false}
          className="dsh-report-panel"
          actions={
            <div className="dsh-report-tools">
              <Segmented
                className="dsh-segmented"
                value={performanceView}
                onChange={setPerformanceView}
                options={[
                  { label: "Call Activity", value: "activity" },
                  { label: "Outcomes", value: "outcomes" },
                ]}
              />
              <Input
                className="dsh-search"
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Search agent"
                onPressEnter={(e) => {
                  const search = e.target.value;
                  setTableFilters((prev) => ({ ...prev, search }));
                  setPerformancePagination((p) => ({ ...p, page: 1 }));
                  loadPerformanceTable({ search, page: 1 });
                }}
              />
              <DatePicker
                className="dsh-datepicker"
                placeholder="Filter by date"
                onChange={(e) => {
                  const date = e ? e.format("YYYY-MM-DD") : null;
                  setTableFilters((prev) => ({ ...prev, date }));
                  setPerformancePagination((p) => ({ ...p, page: 1 }));
                  loadPerformanceTable({ date, page: 1 });
                }}
              />
            </div>
          }
        >
          <div className="dsh-table">
            <Table
              key={performanceView}
              columns={isActivityView ? activityColumns : outcomeColumns}
              dataSource={
                performance_report_table_loading
                  ? Array(5).fill({})
                  : isActivityView
                    ? performance_report_table?.results
                    : validPerformanceTable
              }
              rowKey={(record, index) => record?.id ?? index}
              scroll={{ x: "max-content" }}
              rowClassName={isActivityView ? "" : "clickable-row"}
              pagination={{
                current: performancePagination.page,
                pageSize: performancePagination.page_size,
                total: performance_report_table?.count || 0,
                showSizeChanger: true,
                pageSizeOptions: [5, 10, 20, 50, 100],
                showTotal: (total, range) =>
                  `Showing ${range[0]} - ${range[1]} of ${total} items`,
                onChange: (page, size) => {
                  // A page-size change invalidates the current page number.
                  const nextPage =
                    size !== performancePagination.page_size ? 1 : page;
                  setPerformancePagination({
                    page: nextPage,
                    page_size: size,
                  });
                  loadPerformanceTable({ page: nextPage, page_size: size });
                },
              }}
            />
          </div>
        </PanelCard>
      )}

      {/* ------------------------ action code analytics --------------------- */}
      <PanelCard
        title="Action Code Analytics"
        subtitle="Outcome mix across contact channels"
        icon={<BarChartOutlined />}
        accent="brand"
        actions={
          <Segmented
            className="dsh-segmented"
            value={reportView}
            onChange={setReportView}
            options={[
              { label: "Telecaller", value: "telecaller" },
              { label: "Field Agent", value: "fieldAgent" },
            ]}
          />
        }
      >
        {reportLoading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : (
          <ActionCodeInsight
            key={reportView}
            codes={activeCodes}
            total={activeTotal}
            palette={palette}
            countLabel={reportView === "telecaller" ? "Calls" : "Visits"}
            accent={reportView === "telecaller" ? "#ff6843" : "#6287ff"}
            accentSoft={reportView === "telecaller" ? "#ffb49c" : "#a8bcff"}
          />
        )}
      </PanelCard>

      {/* -------------------------- agency overview ------------------------- */}
      {user?.is_agency && (
        <div className="dsh-split-row">
          <PanelCard
            title="Supervisor Report Overview"
            subtitle="Month-on-month case movement"
            icon={<LineChartOutlined />}
            accent="blue"
            actions={
              <Select
                className="dsh-select"
                value={selectedFilter}
                options={dropdownOptions}
                onChange={handleFilterChange}
              />
            }
          >
            {supervisorReportLoading ? (
              <Skeleton active paragraph={{ rows: 5 }} />
            ) : supervisorReportError || !categories?.length ? (
              <PanelState
                error={supervisorReportError}
                onRetry={() => dispatch(fetchSupervisorReportOverview(query))}
                description="No movement recorded for these filters"
              />
            ) : (
              <TrendAreaChart
                categories={categories}
                seriesData={seriesData}
                colors={overviewColors}
              />
            )}
          </PanelCard>

          <PanelCard
            title="Teamlead Report Overview"
            subtitle={
              teamLeadPortfolio
                ? `${teamLeadPortfolio} · ${teamLeadCut.subtitle}`
                : teamLeadCut.subtitle
            }
            icon={<PieChartOutlined />}
            accent="green"
            className="dsh-teamlead-panel"
            /* a lone option is not a choice - the control is dropped and the
               panel just shows the one cut the payload supports */
            actions={
              statOptions.length > 1 ? (
                <Select
                  className="dsh-select"
                  value={selectedStat}
                  options={statOptions}
                  onChange={handleStatChange}
                />
              ) : null
            }
          >
            {team_lead_loading ? (
              <Skeleton active paragraph={{ rows: 5 }} />
            ) : team_lead_error ? (
              <PanelState
                error={team_lead_error}
                onRetry={() => dispatch(fetchTeamleadOverview(query))}
              />
            ) : (
              <StatusBreakdown
                data={teamLeadCut.data}
                labels={teamLeadCut.labels}
                colors={teamLeadCut.colors}
                percentages={teamLeadCut.percentages}
                centerLabel={teamLeadCut.centerLabel}
                valueFormatter={teamLeadCut.valueFormatter}
              />
            )}
          </PanelCard>
        </div>
      )}

      {/* ----------------------- action code coverage ----------------------- */}
      {user?.is_tc && (
        <PanelCard
          title="Action Code Coverage"
          subtitle="Your coverage against each disposition"
          icon={<PieChartOutlined />}
          accent="blue"
          padded={false}
          className="dsh-report-panel"
        >
          <div className="dsh-table">
            <ActionCodeTable handleRowClick={handleRowClick} />
          </div>
        </PanelCard>
      )}

      <ActionCodesModal
        open={openActionCodes.open}
        data={openActionCodes?.data}
        setOpen={() =>
          setOpenActionCodes((prev) => ({ ...prev, open: false }))
        }
      />
      <TelecallerPerformanceModal
        params={performanceReportRowData?.params}
        open={performanceReportRowData?.open}
        setOpen={() =>
          setPerformanceReportRowData((prev) => ({ ...prev, open: false }))
        }
      />
    </div>
  );
});

export default DashboardPage;
